import './pdfCompatibility'
import React, { useState, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Document, Page, pdfjs } from 'react-pdf'
import type {
  TextItem,
  TextMarkedContent,
} from 'pdfjs-dist/types/src/display/api'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { PDFViewerProps } from './PDFViewer.types'
import { useMobileDetection } from '../../../hooks/useMobileDetection'
import { PDFLoadingState } from './components/PDFLoadingState'
import { PDFErrorState } from './components/PDFErrorState'
import { PDFToolbar } from './components/PDFToolbar'
import { PDFSearchBar } from './components/PDFSearchBar'
import {
  ViewerContainer,
  PDFContainer,
  PageContainer,
} from './PDFViewer.styles'

interface SearchMatch {
  pageNumber: number // 1-indexed
  matchIndex: number // Index within the page
  text: string
}

// Configure PDF.js worker using import.meta.url (Vite-compatible)
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/legacy/build/pdf.worker.min.mjs',
  import.meta.url
).toString()

// Configure PDF.js options for JPEG2000 support (requires WASM)
const pdfOptions = {
  wasmUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/wasm/`,
}

export const PDFViewer: React.FC<PDFViewerProps> = ({
  url,
  initialPage = 0,
  initialRotation = 0,
  // showThumbnails = true,
  showToolbar = true,
  enableSearch = true,
  enableDownload = true,
  // enableFullscreen = true,
  height = '100%',
  width = '100%',
  onLoadSuccess,
  onLoadError,
  onPageChange,
}) => {
  const { t } = useTranslation('ui')
  const isMobile = useMobileDetection()
  const [numPages, setNumPages] = useState<number>(0)
  const [pageNumber, setPageNumber] = useState<number>(initialPage + 1) // react-pdf uses 1-indexed
  const [scale, setScale] = useState<number>(() =>
    typeof window !== 'undefined' && window.innerWidth <= 640 ? 0.5 : 1.0
  )
  const [userRotation, setUserRotation] = useState<number>(initialRotation) // User-controlled rotation
  const [pageInternalRotation, setPageInternalRotation] = useState<number>(0) // PDF's internal rotation
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  // Search state
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchMatches, setSearchMatches] = useState<SearchMatch[]>([])
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0)
  const [pageTexts, setPageTexts] = useState<Map<number, string>>(new Map())

  const pdfDocumentRef = useRef<any>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  // Track the active document URL to force clean unmount/remount cycles
  // This prevents race conditions where Page tries to load from a destroyed document
  // Initialize with processedUrl to avoid issues with React Strict Mode double-rendering
  const [activeDocumentUrl, setActiveDocumentUrl] = useState<string>(() => {
    if (!url) return ''
    // Blob URLs must not be re-encoded
    if (url.startsWith('blob:')) return url
    try {
      const urlObj = new URL(url)
      const pathParts = urlObj.pathname.split('/')
      const encodedParts = pathParts.map((part) =>
        part ? encodeURIComponent(decodeURIComponent(part)) : part
      )
      urlObj.pathname = encodedParts.join('/')
      return urlObj.toString()
    } catch {
      return url
    }
  })

  // Process and encode the URL properly
  const processedUrl = React.useMemo(() => {
    if (!url) return ''
    // Blob URLs must not be re-encoded
    if (url.startsWith('blob:')) return url

    try {
      // Parse the URL
      const urlObj = new URL(url)
      // Get the pathname and split it
      const pathParts = urlObj.pathname.split('/')
      // Encode each part except empty strings
      const encodedParts = pathParts.map((part) =>
        part ? encodeURIComponent(decodeURIComponent(part)) : part
      )
      // Reconstruct the URL
      urlObj.pathname = encodedParts.join('/')
      const processed = urlObj.toString()
      return processed
    } catch (error) {
      console.error('[PDFViewer] Failed to process URL:', error)
      return url
    }
  }, [url])

  // Debug: Log the URL being loaded
  React.useEffect(() => {
    if (!processedUrl) {
      console.error('[PDFViewer] No URL provided!')
      setError(new Error(t('pdfViewer.errors.noUrl')))
      setIsLoading(false)
    }
  }, [processedUrl, t])

  // Track previous URL to detect URL-only changes vs page-only changes
  const prevUrlRef = useRef<string>(processedUrl)
  const isInitialMount = useRef(true)

  // Reset state and abort pending operations when URL changes
  useEffect(() => {
    // Skip the initial mount - activeDocumentUrl is already set via useState initializer
    if (isInitialMount.current) {
      isInitialMount.current = false
      prevUrlRef.current = processedUrl
      return
    }

    const isUrlChange = prevUrlRef.current !== processedUrl
    prevUrlRef.current = processedUrl

    if (isUrlChange && processedUrl) {
      // Abort any pending text extractions
      abortControllerRef.current?.abort()

      // Reset state for new document
      setPageTexts(new Map())
      setNumPages(0)
      setIsLoading(true)
      setError(null)
      pdfDocumentRef.current = null

      // Reset page to initial value for new document
      setPageNumber(initialPage + 1)

      // Reset search state
      setSearchQuery('')
      setSearchMatches([])
      setCurrentMatchIndex(0)

      // Force clean unmount/remount cycle:
      // 1. Clear activeDocumentUrl to unmount Document completely
      // 2. After a tick, set it to new URL to remount fresh
      setActiveDocumentUrl('')
      const timer = setTimeout(() => {
        setActiveDocumentUrl(processedUrl)
      }, 0)

      return () => clearTimeout(timer)
    }
  }, [processedUrl, initialPage])

  // Handle page changes (when initialPage prop changes but URL stays the same)
  useEffect(() => {
    // Only update page if we have a loaded document
    if (numPages > 0) {
      const targetPage = initialPage + 1
      if (targetPage >= 1 && targetPage <= numPages) {
        setPageNumber(targetPage)
      }
    }
  }, [initialPage, numPages])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort()
    }
  }, [])

  const handleDocumentLoadSuccess = (pdf: any) => {
    setIsLoading(false)
    setError(null)
    setNumPages(pdf.numPages)
    pdfDocumentRef.current = pdf

    if (onLoadSuccess) {
      onLoadSuccess(pdf.numPages)
    }
  }

  const handleDocumentLoadError = (error: Error) => {
    console.error('[PDFViewer] PDF load error:', error)
    setIsLoading(false)
    setError(error)

    if (onLoadError) {
      onLoadError(error)
    }
  }

  const handlePageChange = (newPage: number) => {
    setPageNumber(newPage)
    if (onPageChange) {
      onPageChange(newPage - 1) // Convert back to 0-indexed for callback
    }
  }

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.25, 3.0))
  }

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.25, 0.2))
  }

  const handleResetZoom = () => {
    setScale(isMobile ? 0.5 : 1.0)
  }

  const handleRotateLeft = () => {
    setUserRotation((prev) => (prev - 90 + 360) % 360)
  }

  // Combined rotation: PDF internal rotation + user rotation
  const totalRotation = (pageInternalRotation + userRotation) % 360

  const handleRetry = () => {
    setIsLoading(true)
    setError(null)
  }

  // Extract text from a specific page
  const extractPageText = useCallback(
    async (pageNum: number, signal?: AbortSignal): Promise<string> => {
      if (!pdfDocumentRef.current || signal?.aborted) return ''

      try {
        const page = await pdfDocumentRef.current.getPage(pageNum)
        if (signal?.aborted) return ''

        const textContent = await page.getTextContent()
        if (signal?.aborted) return ''

        const pageText = textContent.items
          .map((item: TextItem | TextMarkedContent) => {
            if ('str' in item) {
              return item.str
            }
            return ''
          })
          .join(' ')
        return pageText
      } catch (error) {
        // Suppress errors when aborted OR when document was destroyed
        if (signal?.aborted) return ''

        const errorMessage =
          error instanceof Error ? error.message : String(error)
        // The 'sendWithPromise' error indicates worker was destroyed
        if (
          errorMessage.includes('sendWithPromise') ||
          errorMessage.includes('transport') ||
          errorMessage.includes('destroyed')
        ) {
          // Document was destroyed during extraction - this is expected
          return ''
        }

        console.error(`Error extracting text from page ${pageNum}:`, error)
        return ''
      }
    },
    []
  )

  // Extract text from all pages and store in state
  const extractAllPageTexts = useCallback(async () => {
    if (!pdfDocumentRef.current || !numPages) return

    // Cancel any previous extraction
    abortControllerRef.current?.abort()
    abortControllerRef.current = new AbortController()
    const signal = abortControllerRef.current.signal

    const texts = new Map<number, string>()
    for (let i = 1; i <= numPages; i++) {
      if (signal.aborted) return
      const text = await extractPageText(i, signal)
      texts.set(i, text)
    }

    if (!signal.aborted) {
      setPageTexts(texts)
    }
  }, [numPages, extractPageText])

  // Search across all pages
  const performSearch = useCallback(
    (query: string) => {
      if (!query.trim() || pageTexts.size === 0) {
        setSearchMatches([])
        setCurrentMatchIndex(0)
        return
      }

      const matches: SearchMatch[] = []
      const regex = new RegExp(
        query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
        'gi'
      )

      pageTexts.forEach((text, pageNum) => {
        let match
        let matchIndex = 0
        while ((match = regex.exec(text)) !== null) {
          matches.push({
            pageNumber: pageNum,
            matchIndex,
            text: match[0],
          })
          matchIndex++
        }
      })

      setSearchMatches(matches)
      setCurrentMatchIndex(matches.length > 0 ? 0 : 0)

      // Navigate to first match
      if (matches.length > 0) {
        setPageNumber(matches[0].pageNumber)
      }
    },
    [pageTexts]
  )

  // Handle search query change
  const handleSearchChange = useCallback(
    (query: string) => {
      setSearchQuery(query)
      performSearch(query)
    },
    [performSearch]
  )

  // Navigate to next match
  const handleNextMatch = useCallback(() => {
    if (searchMatches.length === 0) return

    const nextIndex = (currentMatchIndex + 1) % searchMatches.length
    setCurrentMatchIndex(nextIndex)
    setPageNumber(searchMatches[nextIndex].pageNumber)
  }, [searchMatches, currentMatchIndex])

  // Navigate to previous match
  const handlePreviousMatch = useCallback(() => {
    if (searchMatches.length === 0) return

    const prevIndex =
      currentMatchIndex === 0 ? searchMatches.length - 1 : currentMatchIndex - 1
    setCurrentMatchIndex(prevIndex)
    setPageNumber(searchMatches[prevIndex].pageNumber)
  }, [searchMatches, currentMatchIndex])

  // Toggle search bar
  const handleToggleSearch = useCallback(() => {
    setIsSearchOpen((prev) => !prev)
    if (isSearchOpen) {
      // Clear search when closing
      setSearchQuery('')
      setSearchMatches([])
      setCurrentMatchIndex(0)
    }
  }, [isSearchOpen])

  // Custom text renderer for highlighting
  const customTextRenderer = useCallback(
    (textItem: TextItem | TextMarkedContent): string => {
      if (
        !('str' in textItem) ||
        !searchQuery.trim() ||
        searchMatches.length === 0
      ) {
        return 'str' in textItem ? textItem.str : ''
      }

      const text = textItem.str
      const regex = new RegExp(
        `(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
        'gi'
      )

      // Find current page matches
      const currentPageMatches = searchMatches.filter(
        (m) => m.pageNumber === pageNumber
      )
      const isCurrentMatch =
        currentPageMatches.length > 0 &&
        searchMatches[currentMatchIndex]?.pageNumber === pageNumber

      return text.replace(regex, (match) => {
        const className = isCurrentMatch
          ? 'pdf-search-highlight-current'
          : 'pdf-search-highlight'
        return `<mark class="${className}">${match}</mark>`
      })
    },
    [searchQuery, searchMatches, currentMatchIndex, pageNumber]
  )

  // Extract text when document loads
  useEffect(() => {
    if (numPages > 0 && enableSearch) {
      extractAllPageTexts()
    }
  }, [numPages, enableSearch, extractAllPageTexts])

  // Handle Ctrl/Cmd+F to open search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f' && enableSearch) {
        e.preventDefault()
        setIsSearchOpen(true)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [enableSearch])

  // Don't render viewer if no URL
  if (!processedUrl) {
    return (
      <ViewerContainer $height={height} $width={width}>
        <PDFErrorState
          error={new Error(t('pdfViewer.errors.noUrl'))}
          onRetry={undefined}
        />
      </ViewerContainer>
    )
  }

  // Ensure pageNumber is valid (between 1 and numPages)
  // This prevents "Invalid page request" errors during document transitions
  const validPageNumber =
    numPages > 0 ? Math.min(Math.max(1, pageNumber), numPages) : 1

  return (
    <ViewerContainer $height={height} $width={width}>
      {showToolbar && !error && !isLoading && (
        <PDFToolbar
          pageNumber={pageNumber}
          numPages={numPages}
          scale={scale}
          rotation={totalRotation}
          onPageChange={handlePageChange}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onResetZoom={handleResetZoom}
          onRotateLeft={handleRotateLeft}
          enableDownload={enableDownload}
          enableSearch={enableSearch}
          onToggleSearch={handleToggleSearch}
          pdfUrl={processedUrl}
        />
      )}

      {isSearchOpen && enableSearch && !error && !isLoading && (
        <PDFSearchBar
          searchQuery={searchQuery}
          currentMatchIndex={currentMatchIndex}
          totalMatches={searchMatches.length}
          onSearchChange={handleSearchChange}
          onNextMatch={handleNextMatch}
          onPreviousMatch={handlePreviousMatch}
          onClose={handleToggleSearch}
        />
      )}

      {isLoading && <PDFLoadingState />}

      {error && !isLoading && (
        <PDFErrorState error={error} onRetry={handleRetry} />
      )}

      {!error && (
        <PDFContainer $isLoading={isLoading} $hasToolbar={showToolbar}>
          {/* Only render Document when activeDocumentUrl is set to force clean unmount/remount */}
          {activeDocumentUrl && (
            <Document
              key={activeDocumentUrl}
              file={activeDocumentUrl}
              options={pdfOptions}
              onLoadSuccess={handleDocumentLoadSuccess}
              onLoadError={handleDocumentLoadError}
              loading={<PDFLoadingState />}
              error={
                <PDFErrorState
                  error={new Error(t('pdfViewer.errors.loadFailed'))}
                  onRetry={handleRetry}
                />
              }
            >
              {/* Only render Page when document is loaded to prevent getPage on destroyed document */}
              {numPages > 0 && (
                <PageContainer>
                  <Page
                    pageNumber={validPageNumber}
                    scale={scale}
                    rotate={totalRotation}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                    // Render at native device resolution for sharp text on high-DPI displays
                    // Cap at 2 to balance quality vs performance/memory
                    devicePixelRatio={Math.min(window.devicePixelRatio || 1, 2)}
                    customTextRenderer={
                      searchQuery ? customTextRenderer : undefined
                    }
                    onLoadSuccess={(page) => {
                      // Capture the page's internal rotation from PDF metadata
                      if (page.rotate !== pageInternalRotation) {
                        setPageInternalRotation(page.rotate)
                      }
                    }}
                  />
                </PageContainer>
              )}
            </Document>
          )}
        </PDFContainer>
      )}
    </ViewerContainer>
  )
}
